import { useCallback, useEffect, useMemo, useState } from 'react';

import { ehFaseMensalDaRealizacao } from 'pages/RealizacaoDestaque/realizacoes-do-semestre';
import { RealCycle, Step } from 'types/user';

import { EvaluationStep } from './types';

interface UseCycleStageParams {
  cyclesExtended: RealCycle[] | undefined;
  cycleSelected: { _id: string } | undefined;
  realDatetime: number;
  // Cod_etapa EFETIVO do gestor (já resolvido com mad_dupla pelo fluxo) — o kernel só
  // aplica a regra de calendário: cod < 4 vê month, cod 4 trimester, cod 5 semester.
  codEtapaEfetivo: number | undefined;
  // Fase que a navegação pediu (troca de fluxo Diretoria/Rede⇄MAD): mantém o gestor na
  // mesma fase ao trocar de aba. Só vale se estiver entre as fases VISÍVEIS deste fluxo
  // — o nível efetivo pode diferir entre os fluxos (o MAD compõe pela dupla), e nesse
  // caso vale a resolução padrão em vez de uma fase que este fluxo não lista.
  requestedStage?: string;
}

// Port 1:1 do ApreciarRealizacao/index.tsx: ciclos, fases visíveis por nível (JAN-02) e
// janela ativa de apreciação pela hora do servidor (JAN-01). configCycleAndStage NÃO é
// disparado aqui dentro — a página do fluxo o invoca via useEffect quando user e
// internalUser estiverem prontos, como o index fazia.
export function useCycleStage({
  cyclesExtended,
  cycleSelected,
  realDatetime,
  codEtapaEfetivo,
  requestedStage,
}: UseCycleStageParams) {
  const [stages, setStages] = useState<Step[] | undefined>([]);
  const [currentCycle, setCurrentCycle] = useState<string | undefined>();
  const [optionsCycle, setOptionsCycle] = useState<any[]>([]);
  const [activeCycleSelected, setActiveCycleSelected] = useState<any>();
  const [currentStage, setCurrentStage] = useState<string | undefined>();
  const [elementsIndexOf, setElementsIndexOf] = useState<number>();
  const [numberOfElements, setNumberOfElements] = useState(3);

  // CIC-14: trocado o ciclo do app fora da fila (pelo menu), a fila refaz a escolha de ciclo e fase, como na troca
  // feita no próprio seletor: com `currentCycle` e `currentStage` vazios, a página chama `configCycleAndStage` de novo.
  const cicloDoApp = cycleSelected?._id;
  useEffect(() => {
    if (!cicloDoApp || typeof currentCycle === 'undefined' || cicloDoApp === activeCycleSelected) return;
    setActiveCycleSelected(cicloDoApp);
    setCurrentCycle(undefined);
    setCurrentStage(undefined);
  }, [cicloDoApp, currentCycle, activeCycleSelected]);

  const activeStageObj = useMemo(() => {
    if (!Array.isArray(stages) || !currentStage) return undefined;

    return stages.find(x => x._id === currentStage);
  }, [currentStage, stages]);

  const getActiveEvaluationStep: EvaluationStep = useMemo((): EvaluationStep => {
    if (!activeStageObj || !realDatetime) return null;

    if (realDatetime > activeStageObj.date_evaluation_step2_start && realDatetime < activeStageObj.date_evaluation_step2_finish) {
      // Etapa 2 - Validação, Nível 1 e Nível 2 Bonificação MAD
      return 2;
    } else if (
      realDatetime > activeStageObj.date_evaluation_step3_start &&
      realDatetime < activeStageObj.date_evaluation_step3_finish
    ) {
      // Etapa 3
      return 3;
    } else if (
      realDatetime > activeStageObj.date_evaluation_step4_start &&
      realDatetime < activeStageObj.date_evaluation_step4_finish
    ) {
      // Etapa 4 - Nível 2 e pit stop
      return 4;
    } else if (
      realDatetime > activeStageObj.date_evaluation_pitStop_start &&
      realDatetime < activeStageObj.date_evaluation_pitStop_finish
    ) {
      // Pit Stop
      return 4.1;
    } else if (
      realDatetime > activeStageObj.date_evaluation_step5_start &&
      realDatetime < activeStageObj.date_evaluation_step5_finish
    ) {
      // Etapa 5 - Nível 3
      return 5;
    } else {
      return 0;
    }
  }, [activeStageObj, realDatetime]);

  const configCycleAndStage = useCallback(() => {
    if (!cyclesExtended || typeof currentStage !== 'undefined') return;

    const cycleOptions = cyclesExtended.map((cycle: any) => {
      cycle.label = 'Ciclo ' + cycle.title;
      cycle.value = cycle._id;

      return cycle;
    });

    const cycleActive = cyclesExtended.find(x => x._id === cycleSelected?._id);
    const _currentCycle = !!cycleActive ? cycleActive._id : cyclesExtended[0]._id;

    setOptionsCycle(cycleOptions);
    setCurrentCycle(_currentCycle);
    setActiveCycleSelected(cycleSelected?._id ? cycleSelected?._id : _currentCycle);

    const _stages = cyclesExtended
      ?.find(x => x._id === _currentCycle)
      ?.steps?.filter(x => {
        // CAN-03: os ciclos antigos (até o 1S2024) têm só fases mensais, sem `type`; a fila antiga listava todas,
        // qualquer que fosse o nível, mesmo antes de o nível chegar. Mesma regra de `fasesMensaisDoCiclo` da
        // Realização Destaque.
        if (!x.type) return true;

        // codEtapaEfetivo indefinido reproduz o original: nenhuma comparação casa.
        if (typeof codEtapaEfetivo !== 'number') return false;

        if (x.type === 'month' && codEtapaEfetivo < 4) {
          return true;
        } else if (x.type === 'trimester' && codEtapaEfetivo === 4) {
          return true;
        } else if (x.type === 'semester' && codEtapaEfetivo === 5) {
          return true;
        }

        return false;
      });
    setStages(_stages);

    // A fase pedida pela navegação tem precedência; não estando entre as visíveis, cai
    // na resolução padrão do monólito (em avaliação > mês aberto > primeira da lista).
    const _stageActive =
      _stages?.find((item: any) => item._id === requestedStage) ??
      _stages?.find((item: any) => item.status === 'evaluation') ??
      _stages?.find((item: any) => ehFaseMensalDaRealizacao(item) && item.status === 'open') ??
      _stages?.[0];

    const indexOf =
      (Array.isArray(_stages) && _stages?.map(x => x._id).indexOf(_stageActive?._id as string) + 1 > 3
        ? _stages?.map(x => x._id).indexOf(_stageActive?._id as string) + 1
        : 3) || 3;

    setElementsIndexOf(indexOf);
    setNumberOfElements(indexOf);

    setCurrentStage(_stageActive?._id);
  }, [codEtapaEfetivo, currentStage, cycleSelected, cyclesExtended, requestedStage]);

  return {
    stages,
    activeStageObj,
    getActiveEvaluationStep,
    currentCycle,
    setCurrentCycle,
    currentStage,
    setCurrentStage,
    optionsCycle,
    activeCycleSelected,
    setActiveCycleSelected,
    elementsIndexOf,
    numberOfElements,
    setNumberOfElements,
    configCycleAndStage,
  };
}
